/**
 * Optimized Image Generation Service
 * Uses Hugging Face FLUX.1-dev for all image generation with enhanced prompts
 *
 * Strategy:
 * - Character Portraits: FLUX.1-dev with portrait-specific enhancements
 * - Game Scenes: FLUX.1-dev with cinematic enhancements
 * - Cartridge Thumbnails: FLUX.1-dev with game art style prompts
 */

import { fluxImageService } from './fluxImageService';
import { authService } from './authService';

export interface OptimizedImageOptions {
  width?: number;
  height?: number;
  seed?: number;
  style?: string;
  type?: 'portrait' | 'scene' | 'thumbnail' | 'general';
}

class OptimizedImageService {
  private static instance: OptimizedImageService;
  
  private constructor() {}

  static getInstance(): OptimizedImageService {
    if (!OptimizedImageService.instance) {
      OptimizedImageService.instance = new OptimizedImageService();
    }
    return OptimizedImageService.instance;
  }

  /**
   * Generate image using the best strategy for the image type
   */
  async generateImage(prompt: string, options: OptimizedImageOptions = {}): Promise<string> {
    console.log('[OptimizedImageService] Generating image:', { prompt, type: options.type });
    
    // Route to appropriate generator based on type
    switch (options.type) {
      case 'portrait':
        return this.generatePortrait(prompt, options);
      case 'scene':
        return this.generateScene(prompt, options);
      case 'thumbnail':
        return this.generateThumbnail(prompt, options);
      default:
        return this.generateGeneral(prompt, options);
    }
  }

  /**
   * Generate character portraits using Together AI
   */
  private async generatePortrait(prompt: string, options: OptimizedImageOptions): Promise<string> {
    console.log('[OptimizedImageService] Generating portrait');
    
    // Enhance prompt for better portrait results
    const enhancedPrompt = `${prompt}, portrait photography, detailed face, professional lighting, high quality, sharp focus`;
    
    try {
      // Use FLUX.1-dev for portrait generation
      const currentUser = authService.getCurrentUser();
      const userId = currentUser?.id || 'anonymous';
      const result = await fluxImageService.generateImage({
        prompt: enhancedPrompt,
        userId,
        options: {
          width: options.width || 400,
          height: options.height || 400,
          seed: options.seed
        }
      });

      if (!result || !result.imageUrl) {
        throw new Error('[OptimizedImageService] FLUX.1-dev returned invalid result');
      }

      return result.imageUrl;
    } catch (error) {
      console.error('[OptimizedImageService] FLUX.1-dev failed for portrait:', error);
      // Fallback to Pollinations enhanced service
      try {
        const { enhancedPollinationsService } = await import('./enhancedPollinationsService');
        const currentUser = authService.getCurrentUser();
        const userId = currentUser?.id || null;
        const res = await enhancedPollinationsService.generateImage(prompt, userId, {
          width: options.width || 400,
          height: options.height || 400,
          model: 'flux-pro',
          enhance: true
        });
        return res.url;
      } catch (fallbackError) {
        console.warn('[OptimizedImageService] Pollinations fallback failed, using artistic placeholder');
        return this.tryArtisticPlaceholder(prompt, options);
      }
    }
  }

  /**
   * Generate game scenes (needs cinematic quality)
   */
  private async generateScene(prompt: string, options: OptimizedImageOptions): Promise<string> {
    console.log('[OptimizedImageService] Generating scene');

    // Enhance prompt for cinematic scenes
    const enhancedPrompt = `${prompt}, cinematic shot, ultra detailed, volumetric lighting, artstation quality, 8k resolution, masterpiece, epic composition`;

    try {
      // Use FLUX.1-dev for scene generation
      const currentUser = authService.getCurrentUser();
      const userId = currentUser?.id || 'anonymous';
      const result = await fluxImageService.generateImage({
        prompt: enhancedPrompt,
        userId,
        options: {
          width: options.width || 768,
          height: options.height || 512,
          seed: options.seed
        }
      });
      return result.imageUrl;
    } catch (error) {
      console.error('[OptimizedImageService] FLUX.1-dev failed for scene:', error);
      try {
        const { enhancedPollinationsService } = await import('./enhancedPollinationsService');
        const currentUser = authService.getCurrentUser();
        const userId = currentUser?.id || null;
        const res = await enhancedPollinationsService.generateImage(prompt, userId, {
          width: options.width || 768,
          height: options.height || 512,
          model: 'flux-pro',
          enhance: true
        });
        return res.url;
      } catch (fallbackError) {
        const seed = Math.abs(prompt.split('').reduce((a, b) => a + b.charCodeAt(0), 0));
        const fallbackUrl = `https://picsum.photos/seed/cyberpunk${seed}/${options.width || 768}/${options.height || 512}`;
        console.log('[OptimizedImageService] Using fallback image for scene:', fallbackUrl);
        return fallbackUrl;
      }
    }
  }

  /**
   * Generate game cartridge thumbnails
   */
  private async generateThumbnail(prompt: string, options: OptimizedImageOptions): Promise<string> {
    console.log('[OptimizedImageService] Generating thumbnail');

    // Enhance prompt for game art style
    const enhancedPrompt = `${prompt}, game box art, video game cover, digital artwork, vibrant colors, professional game art`;

    // Use specific dimensions for thumbnails
    const thumbnailOptions = {
      ...options,
      width: 400,
      height: 224
    };

    try {
      // Use FLUX.1-dev for thumbnail generation
      const currentUser = authService.getCurrentUser();
      const userId = currentUser?.id || 'anonymous';
      const result = await fluxImageService.generateImage({
        prompt: enhancedPrompt,
        userId,
        options: thumbnailOptions
      });
      return result.imageUrl;
    } catch (error) {
      console.error('[OptimizedImageService] FLUX.1-dev failed for thumbnail:', error);
      try {
        const { enhancedPollinationsService } = await import('./enhancedPollinationsService');
        const currentUser = authService.getCurrentUser();
        const userId = currentUser?.id || null;
        const res = await enhancedPollinationsService.generateImage(prompt, userId, {
          width: thumbnailOptions.width,
          height: thumbnailOptions.height,
          model: 'flux-pro',
          enhance: true
        });
        return res.url;
      } catch (fallbackError) {
        return this.tryArtisticPlaceholder(prompt, { width: thumbnailOptions.width, height: thumbnailOptions.height });
      }
    }
  }

  /**
   * Generate general images
   */
  private async generateGeneral(prompt: string, options: OptimizedImageOptions): Promise<string> {
    console.log('[OptimizedImageService] Generating general image');

    // Add general quality enhancers
    const enhancedPrompt = `${prompt}, high quality, detailed, professional`;

    try {
      // Use FLUX.1-dev for general image generation
      const currentUser = authService.getCurrentUser();
      const userId = currentUser?.id || 'anonymous';
      const result = await fluxImageService.generateImage({
        prompt: enhancedPrompt,
        userId,
        options: {
          width: options.width || 768,
          height: options.height || 512,
          seed: options.seed
        }
      });
      return result.imageUrl;
    } catch (error) {
      console.error('[OptimizedImageService] FLUX.1-dev failed for general image:', error);
      try {
        const { enhancedPollinationsService } = await import('./enhancedPollinationsService');
        const currentUser = authService.getCurrentUser();
        const userId = currentUser?.id || null;
        const res = await enhancedPollinationsService.generateImage(prompt, userId, {
          width: options.width || 768,
          height: options.height || 512,
          model: 'flux-pro',
          enhance: true
        });
        return res.url;
      } catch (fallbackError) {
        return this.tryArtisticPlaceholder(prompt, options);
      }
    }
  }

  // Pollinations fallback removed - using FLUX.1-dev as primary provider

  /**
   * @deprecated - Removed due to authentication issues. Use FLUX.1-dev instead.
   */
  private async tryHuggingFaceFlux(prompt: string, options: OptimizedImageOptions): Promise<string | null> {
    // This method has been deprecated. FLUX.1-dev is now the primary service.
    return null;
  }

  /**
   * Generate artistic placeholder with cyberpunk theme
   */
  private tryArtisticPlaceholder(prompt: string, options: OptimizedImageOptions): string {
    const width = options.width || 768;
    const height = options.height || 512;
    
    // Create a canvas-based placeholder with cyberpunk colors
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    
    if (ctx) {
      // Cyberpunk gradient background
      const gradient = ctx.createLinearGradient(0, 0, width, height);
      gradient.addColorStop(0, '#0f1419');
      gradient.addColorStop(0.5, '#1a293d');
      gradient.addColorStop(1, '#000');
      ctx.fillStyle = gradient;
      ctx.fillRect(0, 0, width, height);
      
      // Add some visual elements
      ctx.strokeStyle = '#00ffff';
      ctx.lineWidth = 2;
      ctx.globalAlpha = 0.3;
      
      // Random lines for tech effect
      for (let i = 0; i < 10; i++) {
        ctx.beginPath();
        ctx.moveTo(Math.random() * width, Math.random() * height);
        ctx.lineTo(Math.random() * width, Math.random() * height);
        ctx.stroke();
      }
      
      // Add text
      ctx.globalAlpha = 0.6;
      ctx.fillStyle = '#00ffff';
      ctx.font = '16px monospace';
      ctx.textAlign = 'center';
      ctx.fillText('Generating Image...', width / 2, height / 2);
      
      return canvas.toDataURL('image/png');
    }
    
    // Fallback to Picsum
    const seed = this.hashCode(prompt);
    return `https://picsum.photos/seed/${seed}/${width}/${height}`;
  }

  private hashCode(str: string): number {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      const char = str.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash;
    }
    return Math.abs(hash);
  }
}

// Export singleton instance
export const optimizedImageService = OptimizedImageService.getInstance();

// Export convenience function with type detection
export async function generateOptimizedImage(
  prompt: string, 
  options: OptimizedImageOptions = {}
): Promise<string> {
  // Auto-detect type based on prompt if not specified
  if (!options.type) {
    const lowerPrompt = prompt.toLowerCase();
    if (lowerPrompt.includes('portrait') || lowerPrompt.includes('character') || lowerPrompt.includes('face')) {
      options.type = 'portrait';
    } else if (lowerPrompt.includes('scene') || lowerPrompt.includes('landscape') || lowerPrompt.includes('environment')) {
      options.type = 'scene';
    } else if (lowerPrompt.includes('thumbnail') || lowerPrompt.includes('cover') || lowerPrompt.includes('box art')) {
      options.type = 'thumbnail';
    }
  }
  
  return optimizedImageService.generateImage(prompt, options);
}
